/**
 * CompactUtterance - 1行コンパクト発話表示
 * 選択中は全文表示、非選択時は truncate。主発言は赤、補助は amber でハイライト。
 */

import type { Utterance } from '@/types/review';

interface CompactUtteranceProps {
  utterance: Utterance;
  isSelected: boolean;
  hasPrimaryFeedback: boolean;
  hasSupportingFeedback: boolean;
  onClick: () => void;
  /** 紐づく指摘の 1-indexed 連番（複数可） */
  feedbackIndices?: number[];
}

function formatShortTimestamp(timestamp: string): string {
  const parts = timestamp.split(':');
  if (parts.length === 3) {
    const hours = Number(parts[0]);
    if (!Number.isNaN(hours) && hours > 0) {
      return `${parts[0]}:${parts[1]}:${parts[2]}`;
    }
    return `${parts[1]}:${parts[2]}`;
  }
  if (parts.length === 2) {
    return `${parts[0]}:${parts[1]}`;
  }
  return timestamp;
}

export function CompactUtterance({
  utterance,
  isSelected,
  hasPrimaryFeedback,
  hasSupportingFeedback,
  onClick,
  feedbackIndices,
}: CompactUtteranceProps) {
  const isFacilitator = utterance.speaker === 'facilitator';
  const shortTime = formatShortTimestamp(utterance.timestamp);

  let containerClass = `flex ${isSelected ? 'items-start' : 'items-center'} px-2 py-1.5 cursor-pointer transition-colors text-sm`;

  if (isSelected) {
    containerClass += ' bg-blue-50 border-l-2 border-blue-500';
  } else if (hasPrimaryFeedback) {
    containerClass += ' bg-red-50 border-l-2 border-red-400';
  } else if (hasSupportingFeedback) {
    containerClass += ' bg-amber-50 border-l-2 border-amber-400';
  } else {
    containerClass += ' hover:bg-gray-50 border-l-2 border-transparent';
  }

  return (
    <div className={containerClass} onClick={onClick}>
      {hasPrimaryFeedback && !isSelected && (
        <span className="text-red-500 mr-1 flex-shrink-0">→</span>
      )}
      {hasSupportingFeedback && !hasPrimaryFeedback && !isSelected && (
        <span className="text-amber-500 mr-1 flex-shrink-0">↳</span>
      )}
      <span className="font-mono text-gray-500 text-xs mr-2 flex-shrink-0 w-14">{shortTime}</span>
      <span className={`mr-1.5 flex-shrink-0 ${isFacilitator ? 'text-blue-500' : 'text-green-500'}`}>
        {isFacilitator ? (
          <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
          </svg>
        ) : (
          <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
            <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z" />
          </svg>
        )}
      </span>
      <span
        className={`${isSelected ? 'whitespace-pre-wrap break-words flex-1' : 'truncate'} ${
          hasPrimaryFeedback
            ? 'text-red-800'
            : hasSupportingFeedback
              ? 'text-amber-900'
              : 'text-gray-700'
        }`}
      >
        {utterance.text}
      </span>
      {hasPrimaryFeedback && (
        <span className="ml-2 flex-shrink-0 rounded border border-red-200 bg-red-100 px-1.5 py-0.5 text-[10px] font-medium text-red-700">
          {feedbackIndices && feedbackIndices.length > 0
            ? feedbackIndices.map((n) => `#${n}`).join(' ')
            : '主'}
        </span>
      )}
      {hasSupportingFeedback && !hasPrimaryFeedback && (
        <span className="ml-2 flex-shrink-0 rounded border border-amber-200 bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-700">
          {feedbackIndices && feedbackIndices.length > 0
            ? feedbackIndices.map((n) => `#${n}`).join(' ')
            : '補助'}
        </span>
      )}
    </div>
  );
}
